import { arrayIncludes } from '@/utils/primitives'

import type { Column } from '~shared/rundown/column'
import { CueType } from '~shared/rundown/cue'
import type { Cue } from '~shared/rundown/cue'

import { HEADING_COLUMN_ID } from '@/entities/rundown'

import {
  KeyboardNavigationDirection,
  KeyboardNavigationInput,
} from './model/enums'
import type {
  KeyboardNavigationJumpPayload,
  KeyboardNavigationFocusPayload,
} from './model/events'
import type { KeyboardNavigationConstructor } from './model/params'

export class KeyboardNavigationHandler {
  cues: readonly Cue[] = []
  columns: readonly Column[] = []

  constructor({ cues, columns }: KeyboardNavigationConstructor) {
    this.cues = cues
    this.columns = columns
  }

  public jump({
    direction,
    fromCueId,
    fromColumnId,
    fromInputType,
  }: KeyboardNavigationJumpPayload):
    | KeyboardNavigationFocusPayload
    | undefined {
    const cueIndex = this.indexOfCue(fromCueId)

    // Tab navigation (forward/back) follows a linear order across the grid
    if (direction === KeyboardNavigationDirection.FORWARD) {
      return this.navigateForward(cueIndex, fromColumnId, fromInputType)
    }
    if (direction === KeyboardNavigationDirection.REVERSE) {
      return this.navigateReverse(cueIndex, fromColumnId, fromInputType)
    }

    if (fromColumnId === HEADING_COLUMN_ID) {
      return this.navigateFromHeading(direction, cueIndex, fromInputType)
    }

    const colIndex = this.indexOfColumn(fromColumnId as string)
    return this.navigateFromCell(direction, cueIndex, colIndex)
  }

  private navigateFromHeading(
    direction: KeyboardNavigationDirection,
    cueIndex: number,
    fromInputType: KeyboardNavigationInput,
  ): KeyboardNavigationFocusPayload | undefined {
    const isNavigatingFromTitle =
      fromInputType === KeyboardNavigationInput.TITLE
    const isNavigatingFromTitleOnlyCue = this.isTitleOnlyCue(cueIndex)

    switch (direction) {
      case KeyboardNavigationDirection.UP: {
        // From subtitle within the same cue: go to this cue's title
        if (!isNavigatingFromTitle) {
          return {
            focusPosition: 'end',
            cueId: this.cues[cueIndex].id,
            columnId: HEADING_COLUMN_ID,
            inputType: KeyboardNavigationInput.TITLE,
          }
        }

        const targetCue = Math.max(0, cueIndex - 1)

        return {
          focusPosition: 'end',
          cueId: this.cues[targetCue].id,
          columnId: HEADING_COLUMN_ID,
          inputType: this.hasSubtitle(targetCue)
            ? KeyboardNavigationInput.SUBTITLE
            : KeyboardNavigationInput.TITLE,
        }
      }
      case KeyboardNavigationDirection.DOWN: {
        if (isNavigatingFromTitle) {
          if (isNavigatingFromTitleOnlyCue) {
            // Special heading/group cue: move to next cue title
            const targetCue = Math.min(this.cues.length - 1, cueIndex + 1)
            return {
              focusPosition: 'start',
              cueId: this.cues[targetCue].id,
              columnId: HEADING_COLUMN_ID,
              inputType: KeyboardNavigationInput.TITLE,
            }
          }
          // Regular cue: move into current cue subtitle if present, else keep title
          return {
            focusPosition: 'start',
            cueId: this.cues[cueIndex].id,
            columnId: HEADING_COLUMN_ID,
            inputType: this.hasSubtitle(cueIndex)
              ? KeyboardNavigationInput.SUBTITLE
              : KeyboardNavigationInput.TITLE,
          }
        }
        // From subtitle go to next cue title if exists
        const targetCue = Math.min(this.cues.length - 1, cueIndex + 1)
        return {
          focusPosition: 'start',
          cueId: this.cues[targetCue].id,
          columnId: HEADING_COLUMN_ID,
          inputType: KeyboardNavigationInput.TITLE,
        }
      }
      case KeyboardNavigationDirection.LEFT: {
        // Wrap to last cell of cue above if exists; otherwise stay in place
        if (cueIndex > 0 && this.columns.length > 0) {
          const prevCue = cueIndex - 1
          return {
            focusPosition: 'end',
            cueId: this.cues[prevCue].id,
            columnId: this.columns[this.columns.length - 1].id,
            inputType: KeyboardNavigationInput.CELL,
          }
        }
        // No cue above or no columns; keep focus on current title/subtitle
        return {
          focusPosition: isNavigatingFromTitle ? 'start' : 'end',
          cueId: this.cues[cueIndex].id,
          columnId: HEADING_COLUMN_ID,
          inputType: fromInputType,
        }
      }
      case KeyboardNavigationDirection.RIGHT: {
        if (isNavigatingFromTitle && isNavigatingFromTitleOnlyCue) {
          // For heading/group title: move to next cue title
          const targetCue = Math.min(this.cues.length - 1, cueIndex + 1)
          return {
            focusPosition: 'start',
            cueId: this.cues[targetCue].id,
            columnId: HEADING_COLUMN_ID,
            inputType: KeyboardNavigationInput.TITLE,
          }
        }
        // Move to first cell of current cue
        if (this.columns.length > 0) {
          return {
            focusPosition: 'start',
            cueId: this.cues[cueIndex].id,
            columnId: this.columns[0].id,
            inputType: KeyboardNavigationInput.CELL,
          }
        }
        // No columns available; keep focus in heading
        return {
          focusPosition: isNavigatingFromTitle ? 'end' : 'start',
          cueId: this.cues[cueIndex].id,
          columnId: HEADING_COLUMN_ID,
          inputType: fromInputType,
        }
      }
    }
  }

  private navigateFromCell(
    direction: KeyboardNavigationDirection,
    cueIndex: number,
    colIndex: number,
  ): KeyboardNavigationFocusPayload | undefined {
    const firstCol = 0
    const lastCol = Math.max(0, this.columns.length - 1)

    switch (direction) {
      case KeyboardNavigationDirection.UP: {
        const targetCue = Math.max(0, cueIndex - 1)
        if (this.isTitleOnlyCue(targetCue)) {
          // If the target row is a heading/group (no cells), focus its title
          return {
            focusPosition: 'end',
            cueId: this.cues[targetCue].id,
            columnId: HEADING_COLUMN_ID,
            inputType: KeyboardNavigationInput.TITLE,
          }
        }
        return {
          focusPosition: 'end',
          cueId: this.cues[targetCue].id,
          columnId: this.columns[colIndex].id,
          inputType: KeyboardNavigationInput.CELL,
        }
      }
      case KeyboardNavigationDirection.DOWN: {
        const targetCue = Math.min(this.cues.length - 1, cueIndex + 1)

        if (this.isTitleOnlyCue(targetCue)) {
          // If the target row is a heading/group (no cells), focus its title
          return {
            focusPosition: 'start',
            cueId: this.cues[targetCue].id,
            columnId: HEADING_COLUMN_ID,
            inputType: KeyboardNavigationInput.TITLE,
          }
        }
        return {
          focusPosition: 'start',
          cueId: this.cues[targetCue].id,
          columnId: this.columns[colIndex].id,
          inputType: KeyboardNavigationInput.CELL,
        }
      }
      case KeyboardNavigationDirection.LEFT: {
        if (colIndex === firstCol) {
          // Move to heading subtitle of current cue if exists, otherwise title
          return {
            focusPosition: 'end',
            cueId: this.cues[cueIndex].id,
            columnId: HEADING_COLUMN_ID,
            inputType: this.hasSubtitle(cueIndex)
              ? KeyboardNavigationInput.SUBTITLE
              : KeyboardNavigationInput.TITLE,
          }
        }

        // Move to previous cell
        return {
          focusPosition: 'end',
          cueId: this.cues[cueIndex].id,
          columnId: this.columns[colIndex - 1].id,
          inputType: KeyboardNavigationInput.CELL,
        }
      }
      case KeyboardNavigationDirection.RIGHT: {
        if (colIndex === lastCol) {
          // Move to heading title of next cue if exists; else keep to last col
          const targetCue = Math.min(this.cues.length - 1, cueIndex + 1)
          return {
            focusPosition: 'start',
            cueId: this.cues[targetCue].id,
            columnId: HEADING_COLUMN_ID,
            inputType: KeyboardNavigationInput.TITLE,
          }
        }
        // Move to next cell
        return {
          focusPosition: 'start',
          cueId: this.cues[cueIndex].id,
          columnId: this.columns[colIndex + 1].id,
          inputType: KeyboardNavigationInput.CELL,
        }
      }
    }
  }

  private navigateForward(
    cueIndex: number,
    fromColumnId: string,
    fromInputType: KeyboardNavigationInput,
  ): KeyboardNavigationFocusPayload | undefined {
    const hasCols = this.columns.length > 0
    const firstColId = hasCols ? this.columns[0].id : undefined

    // Map current position to a linear step within the cue
    const isHeading = fromColumnId === HEADING_COLUMN_ID

    // Within current cue
    if (isHeading) {
      if (fromInputType === KeyboardNavigationInput.TITLE) {
        if (this.isTitleOnlyCue(cueIndex)) {
          // Heading/group: go to next cue title
          if (cueIndex >= this.cues.length - 1) return undefined
          return {
            focusPosition: 'start',
            cueId: this.cues[cueIndex + 1].id,
            columnId: HEADING_COLUMN_ID,
            inputType: KeyboardNavigationInput.TITLE,
          }
        }
        // Regular cue: try subtitle, else first cell, else next cue title
        if (this.hasSubtitle(cueIndex)) {
          return {
            focusPosition: 'start',
            cueId: this.cues[cueIndex].id,
            columnId: HEADING_COLUMN_ID,
            inputType: KeyboardNavigationInput.SUBTITLE,
          }
        }
        if (firstColId) {
          return {
            focusPosition: 'start',
            cueId: this.cues[cueIndex].id,
            columnId: firstColId,
            inputType: KeyboardNavigationInput.CELL,
          }
        }
        if (cueIndex >= this.cues.length - 1) return undefined
        return {
          focusPosition: 'start',
          cueId: this.cues[cueIndex + 1].id,
          columnId: HEADING_COLUMN_ID,
          inputType: KeyboardNavigationInput.TITLE,
        }
      }

      // From subtitle
      if (fromInputType === KeyboardNavigationInput.SUBTITLE) {
        if (firstColId) {
          return {
            focusPosition: 'start',
            cueId: this.cues[cueIndex].id,
            columnId: firstColId,
            inputType: KeyboardNavigationInput.CELL,
          }
        }
        if (cueIndex >= this.cues.length - 1) return undefined
        return {
          focusPosition: 'start',
          cueId: this.cues[cueIndex + 1].id,
          columnId: HEADING_COLUMN_ID,
          inputType: KeyboardNavigationInput.TITLE,
        }
      }
    } else {
      // From a cell
      const colIndex = this.indexOfColumn(fromColumnId as string)
      const lastColIndex = Math.max(0, this.columns.length - 1)
      if (colIndex < lastColIndex) {
        return {
          focusPosition: 'start',
          cueId: this.cues[cueIndex].id,
          columnId: this.columns[colIndex + 1].id,
          inputType: KeyboardNavigationInput.CELL,
        }
      }
      // From last column -> next cue title
      if (cueIndex >= this.cues.length - 1) return undefined
      return {
        focusPosition: 'start',
        cueId: this.cues[cueIndex + 1].id,
        columnId: HEADING_COLUMN_ID,
        inputType: KeyboardNavigationInput.TITLE,
      }
    }

    return undefined
  }

  private navigateReverse(
    cueIndex: number,
    fromColumnId: string,
    fromInputType: KeyboardNavigationInput,
  ): KeyboardNavigationFocusPayload | undefined {
    const hasCols = this.columns.length > 0
    const lastColId = hasCols
      ? this.columns[this.columns.length - 1].id
      : undefined

    const isHeading = fromColumnId === HEADING_COLUMN_ID

    if (isHeading) {
      if (fromInputType === KeyboardNavigationInput.TITLE) {
        // Go to previous cue's last focusable
        if (cueIndex <= 0) return undefined
        const prevIndex = cueIndex - 1
        if (this.isTitleOnlyCue(prevIndex)) {
          return {
            focusPosition: 'end',
            cueId: this.cues[prevIndex].id,
            columnId: HEADING_COLUMN_ID,
            inputType: KeyboardNavigationInput.TITLE,
          }
        }
        // Prefer last cell if any columns, else subtitle if exists, else title
        if (lastColId) {
          return {
            focusPosition: 'end',
            cueId: this.cues[prevIndex].id,
            columnId: lastColId,
            inputType: KeyboardNavigationInput.CELL,
          }
        }
        if (this.hasSubtitle(prevIndex)) {
          return {
            focusPosition: 'end',
            cueId: this.cues[prevIndex].id,
            columnId: HEADING_COLUMN_ID,
            inputType: KeyboardNavigationInput.SUBTITLE,
          }
        }
        return {
          focusPosition: 'end',
          cueId: this.cues[prevIndex].id,
          columnId: HEADING_COLUMN_ID,
          inputType: KeyboardNavigationInput.TITLE,
        }
      }

      // From subtitle -> title of same cue
      if (fromInputType === KeyboardNavigationInput.SUBTITLE) {
        return {
          focusPosition: 'end',
          cueId: this.cues[cueIndex].id,
          columnId: HEADING_COLUMN_ID,
          inputType: KeyboardNavigationInput.TITLE,
        }
      }
    } else {
      // From a cell
      const colIndex = this.indexOfColumn(fromColumnId as string)
      if (colIndex > 0) {
        return {
          focusPosition: 'end',
          cueId: this.cues[cueIndex].id,
          columnId: this.columns[colIndex - 1].id,
          inputType: KeyboardNavigationInput.CELL,
        }
      }
      // From first column -> subtitle if exists, else title of same cue
      if (this.hasSubtitle(cueIndex)) {
        return {
          focusPosition: 'end',
          cueId: this.cues[cueIndex].id,
          columnId: HEADING_COLUMN_ID,
          inputType: KeyboardNavigationInput.SUBTITLE,
        }
      }
      return {
        focusPosition: 'end',
        cueId: this.cues[cueIndex].id,
        columnId: HEADING_COLUMN_ID,
        inputType: KeyboardNavigationInput.TITLE,
      }
    }

    return undefined
  }

  private indexOfCue(cueId: string): number {
    return Math.max(
      0,
      Math.min(
        this.cues.findIndex(({ id }) => id === cueId),
        this.cues.length - 1,
      ),
    )
  }

  private indexOfColumn(columnId: string): number {
    const idx = this.columns.findIndex(({ id }) => id === columnId)
    return Math.max(0, Math.min(idx, this.columns.length - 1))
  }

  private hasSubtitle(cueIndex: number): boolean {
    const cue = this.cues[cueIndex]

    return !!cue && !arrayIncludes([CueType.HEADING, CueType.GROUP], cue.type)
  }

  private isTitleOnlyCue(cueIndex: number): boolean {
    const type = this.cues[cueIndex]?.type

    return type === CueType.HEADING || type === CueType.GROUP
  }
}
